import { inject, Injectable, Injector } from '@angular/core';
import { LocalStorageService } from '@web/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { blank, Nillable } from '@web/utils';
import { catchNotFoundAndNavigateToNotFoundPage } from '@web/common';
import { PublicProductApiService } from '../api/services/public-product-api.service';
import { PublicProductStatusType } from '../enums/public-product-status-type';
import { PublicProductAuthorizeRequest } from '../api/requests/public-product-authorize.request';
import { PublicProductDetailsResponse } from '../api/responses/public-product-details.response';

@Injectable({
  providedIn: 'root',
})
export class PublicProductAuthService {
  private readonly storage = inject(LocalStorageService);
  private readonly publicProductApi = inject(PublicProductApiService);

  private readonly injector = inject(Injector);
  private readonly StorageKeyPrefix = '6eb645f9-ffe1-4ba6-bde1-a3115ec7ae06';

  public authorize(slug: string, password: string): Observable<void> {
    return this.publicProductApi
      .authorize(slug, new PublicProductAuthorizeRequest(password))
      .pipe(
        map((response) => response.accessToken),
        tap((token) =>
          this.storage.setItem(this.createStorageKey(slug), token),
        ),
        catchNotFoundAndNavigateToNotFoundPage(this.injector),
        blank(),
      );
  }

  public fetchAuthorizedDetails(
    slug: string,
    cacheable = true,
  ): Observable<PublicProductDetailsResponse> {
    return this.getToken(slug).pipe(
      switchMap((token) =>
        this.publicProductApi.fetchDetailsBySlug(slug, token, cacheable),
      ),
      catchNotFoundAndNavigateToNotFoundPage(this.injector),
    );
  }

  public isAuthorizationRequired(slug: string): Observable<boolean> {
    return this.publicProductApi.checkVisibilityBySlug(slug, true).pipe(
      map((response) => response.status),
      map((status) => status === PublicProductStatusType.Protected),
      catchNotFoundAndNavigateToNotFoundPage(this.injector),
    );
  }

  private getToken(slug: string): Observable<Nillable<string>> {
    const token = this.storage.getItem(this.createStorageKey(slug));

    return of(token);
  }

  private createStorageKey(slug: string): string {
    return `${this.StorageKeyPrefix}:${slug}`;
  }
}
